import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, getAllServicingDocuments, setAdminConfig } from 'services'
import { Button, Input2 } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

/** Company-level fallbacks for the Extension agreement's lender signature block. */
export interface ExtensionDefaultsConfig {
  lenderSigner: string
  lenderTitle: string
  allowEmailWithoutSignature: boolean
  extensionDateFromMaturity: boolean
  defaultTermMonths: number
  extensionDocumentNames: string[]
  requireFee: boolean
}

const signerInput = (value: string): InputType => ({
  inputType: 'text',
  title: 'Lender Signer',
  value,
})

const titleInput = (value: string): InputType => ({
  inputType: 'text',
  title: 'Lender Title',
  value,
})

const emailSendInput = (value: boolean): InputType => ({
  inputType: 'checkbox',
  title: 'Offer "Email extension document to borrower" on the extensions grid',
  value,
  span: 'full',
})

const requireFeeInput = (value: boolean): InputType => ({
  inputType: 'checkbox',
  title: 'Require an Extension Fee on every new extension',
  value,
  span: 'full',
})

const dateFromMaturityInput = (value: boolean): InputType => ({
  inputType: 'checkbox',
  title: 'Prefill Extension Date with the current maturity date instead of today',
  value,
  span: 'full',
})

const termMonthsInput = (value: string): InputType => ({
  inputType: 'text',
  type: 'number',
  title: 'Default Extension Term (months)',
  value,
  tooltip: 'Added to the current maturity date to prefill New Maturity Date. 0 leaves it blank.',
})

/**
 * Who signs an extension agreement for the lender, when the loan itself does not say.
 *
 * The Extension screen prefills Lender Signer and Lender Title from the loan's own Lender party
 * (Parties > Lender > First Name / Last Name / Title). That is the right answer when a loan names
 * an individual. A servicer whose extensions are all signed by the same officer has nothing to put
 * there loan by loan, so both fields open blank on every extension and get typed in by hand.
 *
 * What is set here fills that gap and nothing more: a loan that names its own lender signer still
 * wins, and leaving these blank keeps the behaviour exactly as it was.
 */
export const ExtensionDefaultsConfiguration = () => {
  const [isLoading, setLoading] = useState(true)
  const [lenderSigner, setLenderSigner] = useState('')
  const [lenderTitle, setLenderTitle] = useState('')
  const [allowEmailWithoutSignature, setAllowEmailWithoutSignature] = useState(true)
  const [requireFee, setRequireFee] = useState(true)
  const [extensionDateFromMaturity, setExtensionDateFromMaturity] = useState(false)
  const [defaultTermMonths, setDefaultTermMonths] = useState('0')
  const [extensionDocumentNames, setExtensionDocumentNames] = useState<string[]>([])
  const [catalogNames, setCatalogNames] = useState<string[]>([])
  const [catalogFailed, setCatalogFailed] = useState(false)
  const [documentFilter, setDocumentFilter] = useState('')

  useEffect(() => {
    fetchDefaults()
  }, [])

  const fetchDefaults = async () => {
    setLoading(true)
    try {
      const [data, catalogRes] = await Promise.all([
        getAdminConfig('extensionDefaults'),
        getAllServicingDocuments().catch(() => null),
      ])
      setLenderSigner(`${data?.lenderSigner ?? ''}`)
      setLenderTitle(`${data?.lenderTitle ?? ''}`)
      // Matches the server: only an explicit false turns the send off.
      setAllowEmailWithoutSignature(data?.allowEmailWithoutSignature !== false)
      setRequireFee(data?.requireFee !== false)
      setExtensionDateFromMaturity(data?.extensionDateFromMaturity === true)
      setDefaultTermMonths(`${data?.defaultTermMonths ?? 0}`)
      const savedNames = Array.isArray(data?.extensionDocumentNames)
        ? data.extensionDocumentNames.map((name: any) => `${name ?? ''}`.trim()).filter(Boolean)
        : []
      setExtensionDocumentNames(savedNames)

      if (catalogRes?.documents) {
        const names = Array.from(
          new Set(
            (catalogRes.documents || [])
              .map((doc: any) => `${doc?.name ?? ''}`.trim())
              .filter(Boolean),
          ),
        ).sort((a, b) => a.localeCompare(b))
        setCatalogNames(names)
        setCatalogFailed(false)
      } else {
        // Keep the saved checklist on screen so a Docgen outage cannot be saved over as empty.
        setCatalogNames([])
        setCatalogFailed(true)
      }
    } catch (error) {
      console.error('Error fetching extension defaults:', error)
    } finally {
      setLoading(false)
    }
  }

  const listedNames = useMemo(() => {
    const seen = new Set<string>()
    const names: string[] = []
    ;[...catalogNames, ...extensionDocumentNames].forEach((name) => {
      if (seen.has(name)) return
      seen.add(name)
      names.push(name)
    })
    return names.sort((a, b) => a.localeCompare(b))
  }, [catalogNames, extensionDocumentNames])

  const filteredNames = useMemo(() => {
    const query = documentFilter.trim().toLowerCase()
    if (!query) return listedNames
    return listedNames.filter((name) => name.toLowerCase().includes(query))
  }, [listedNames, documentFilter])

  const checkedSet = useMemo(() => new Set(extensionDocumentNames), [extensionDocumentNames])

  const onToggleDocument = (name: string, checked: boolean) => {
    setExtensionDocumentNames((prev) => {
      if (checked) return prev.includes(name) ? prev : [...prev, name]
      return prev.filter((item) => item !== name)
    })
  }

  const onSave = async () => {
    if (catalogFailed) {
      toast('Document catalog could not be loaded. Fix Docgen connectivity before saving the checklist.', {
        type: 'error',
      })
      return
    }
    const cleaned = {
      lenderSigner: lenderSigner.trim(),
      lenderTitle: lenderTitle.trim(),
      allowEmailWithoutSignature,
      requireFee,
      extensionDateFromMaturity,
      defaultTermMonths: Math.max(0, Math.floor(Number(defaultTermMonths) || 0)),
      extensionDocumentNames,
    }
    setLoading(true)
    try {
      await setAdminConfig('extensionDefaults', cleaned)
      setLenderSigner(cleaned.lenderSigner)
      setLenderTitle(cleaned.lenderTitle)
      setDefaultTermMonths(`${cleaned.defaultTermMonths}`)
      toast('Extension defaults saved.', { type: 'success' })
    } catch (error) {
      console.error('Error saving extension defaults:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Extension Defaults</h2>
        <p className="text-sm text-gray-600 mt-1">
          Who signs a loan extension agreement for the lender. These prefill the Lender Signer and Lender Title fields
          on every extension, so they do not have to be typed in each time.
        </p>
        <p className="text-sm text-gray-600 mt-1">
          A loan that names its own lender representative under Parties still wins over what is set here. Leave these
          blank to keep reading the signer from the loan alone.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <RenderInput input={signerInput(lenderSigner)} Key="lenderSigner" onChange={(_: string, value: string) => setLenderSigner(value)} />
        <RenderInput input={titleInput(lenderTitle)} Key="lenderTitle" onChange={(_: string, value: string) => setLenderTitle(value)} />
      </div>

      <div className="mt-6 max-w-2xl">
        <h3 className="text-base font-semibold text-gray-800">Extension dates</h3>
        <p className="text-sm text-gray-600 mt-1 mb-2">
          What a new extension opens with. An agreement is usually dated as of the maturity date it extends, and most
          extensions here run the same term. Both stay editable on every extension.
        </p>
        <RenderInput
          input={dateFromMaturityInput(extensionDateFromMaturity)}
          Key="extensionDateFromMaturity"
          onChange={(_: string, value: boolean) => setExtensionDateFromMaturity(value)}
        />
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <RenderInput
            input={termMonthsInput(defaultTermMonths)}
            Key="defaultTermMonths"
            onChange={(_: string, value: string) => setDefaultTermMonths(value)}
          />
        </div>
      </div>

      <div className="mt-6 max-w-2xl">
        <h3 className="text-base font-semibold text-gray-800">Extension fee</h3>
        <p className="text-sm text-gray-600 mt-1 mb-2">
          Untick this to allow an extension to be saved with no fee. A free extension sends no fee payment request and
          shows No Fee on the grid.
        </p>
        <RenderInput
          input={requireFeeInput(requireFee)}
          Key="requireFee"
          onChange={(_: string, value: boolean) => setRequireFee(value)}
        />
      </div>

      <div className="mt-6 max-w-2xl">
        <h3 className="text-base font-semibold text-gray-800">Sending the agreement</h3>
        <p className="text-sm text-gray-600 mt-1 mb-2">
          The extensions grid offers two ways to send: email the document as an attachment, or send it out for
          signature. The first asks for nothing back, so the agreement is left with no signature record and the signed
          copy has to be uploaded by hand. Untick this if extensions here always go out for e-signature, and that
          button is hidden on every loan.
        </p>
        <RenderInput
          input={emailSendInput(allowEmailWithoutSignature)}
          Key="allowEmailWithoutSignature"
          onChange={(_: string, value: boolean) => setAllowEmailWithoutSignature(value)}
        />
      </div>

      <div className="mt-6 max-w-2xl">
        <h3 className="text-base font-semibold text-gray-800">Extension documents</h3>
        <p className="text-sm text-gray-600 mt-1 mb-2">
          Which Docgen servicing templates appear in the Extension Document picker on Loan Extensions. Leave unchecked
          templates off that list; Servicing Documents still shows the full catalog. Until any are checked, the picker
          is empty.
        </p>
        {catalogFailed && (
          <p className="text-sm text-red-600 mb-2">
            The document catalog could not be loaded. Saved selections are still shown below; fix Docgen and reload
            before saving.
          </p>
        )}
        <div className="mb-2">
          <Input2
            title="Filter templates"
            value={documentFilter}
            onChange={(value) => setDocumentFilter(value)}
          />
        </div>
        <div className="border border-gray-200 rounded-md max-h-72 overflow-y-auto divide-y divide-gray-100">
          {filteredNames.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-500">
              {listedNames.length === 0
                ? catalogFailed
                  ? 'No saved template names and the catalog is unavailable.'
                  : 'No servicing templates are available from Docgen.'
                : 'No templates match this filter.'}
            </div>
          ) : (
            filteredNames.map((name) => {
              const missingFromCatalog = !catalogNames.includes(name)
              return (
                <label key={name} className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded border-gray-300"
                    checked={checkedSet.has(name)}
                    onChange={(e) => onToggleDocument(name, e.target.checked)}
                  />
                  <span>
                    {name}
                    {missingFromCatalog && (
                      <span className="ml-2 text-xs text-amber-700">(no longer in Docgen catalog)</span>
                    )}
                  </span>
                </label>
              )
            })
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {extensionDocumentNames.length} selected
          {documentFilter.trim() ? ` · showing ${filteredNames.length} of ${listedNames.length}` : ''}
        </p>
      </div>

      <div className="mt-4">
        <Button onClick={onSave} loading={isLoading}>
          Save
        </Button>
      </div>
    </div>
  )
}
